import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAccountingReviewSummary,
  listAccountingReviewDocumentRequests,
  sendAccountingReviewDocumentRequestReminder,
  updateAccountingReviewDocumentRequestFollowup,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const followupTone = {
  waiting_for_client: "border-slate-200 bg-slate-50 text-slate-700",
  reminder_sent: "border-sky-200 bg-sky-50 text-sky-700",
  follow_up_needed: "border-amber-200 bg-amber-50 text-amber-700",
  ready_for_review: "border-emerald-200 bg-emerald-50 text-emerald-700",
  completed: "border-slate-200 bg-white text-slate-600",
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatMoney(cents) {
  const value = Number(cents || 0);
  if (!value) return "$0/month";
  return `$${Math.round(value / 100)}/month`;
}

function keyFor(row) {
  return `${row.business_id}:${row.salon_id}`;
}

function requestKey(request) {
  return `${request.business_id}:${request.salon_id}`;
}

export default function SalonBusinessCpaClientWorkboard() {
  const { token } = useAuth();
  const [summaryRows, setSummaryRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [followupOptions, setFollowupOptions] = useState([]);
  const [filter, setFilter] = useState("active");
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, requestData] = await Promise.all([
        getAccountingReviewSummary(token),
        listAccountingReviewDocumentRequests(token, { limit: 250 }),
      ]);
      setSummaryRows(Array.isArray(summaryData?.salons) ? summaryData.salons : []);
      setRequests(Array.isArray(requestData?.requests) ? requestData.requests : []);
      setFollowupOptions(
        Array.isArray(requestData?.followup_status_options)
          ? requestData.followup_status_options
          : []
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load the client workboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const requestsByClient = useMemo(() => {
    return requests.reduce((map, request) => {
      const key = requestKey(request);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(request);
      return map;
    }, new Map());
  }, [requests]);

  const clients = useMemo(() => {
    return summaryRows
      .map((row) => {
        const clientRequests = requestsByClient.get(keyFor(row)) || [];
        const openRequests = clientRequests.filter(
          (request) => !["done", "cancelled"].includes(request.status)
        );
        const answersNeeded = openRequests.filter(
          (request) => request.has_unanswered_questions
        );
        const followupNeeded = openRequests.filter(
          (request) => request.followup_status === "follow_up_needed"
        );
        const remindersSent = openRequests.reduce(
          (total, request) => total + Number(request.reminder_count || 0),
          0
        );
        return {
          ...row,
          requests: clientRequests,
          openRequests,
          answersNeeded,
          followupNeeded,
          remindersSent,
        };
      })
      .filter((row) => {
        if (filter === "setup") return !row.onboarding_completed || row.missing_setup_items?.length;
        if (filter === "package") return row.package_mismatch || row.recommended_package_custom_quote;
        if (filter === "requests") return row.openRequests.length > 0;
        if (filter === "followup") return row.followupNeeded.length > 0;
        if (filter === "active") {
          return (
            !row.onboarding_completed ||
            row.package_mismatch ||
            row.recommended_package_custom_quote ||
            row.openRequests.length > 0 ||
            row.received_document_count > 0 ||
            row.reviewing_document_count > 0
          );
        }
        return true;
      });
  }, [filter, requestsByClient, summaryRows]);

  const totals = useMemo(() => {
    return clients.reduce(
      (next, row) => {
        next.clients += 1;
        next.openRequests += row.openRequests.length;
        next.followupNeeded += row.followupNeeded.length;
        next.answersNeeded += row.answersNeeded.length;
        if (row.package_mismatch || row.recommended_package_custom_quote) next.packageReview += 1;
        return next;
      },
      { clients: 0, openRequests: 0, followupNeeded: 0, answersNeeded: 0, packageReview: 0 }
    );
  }, [clients]);

  const handleReminder = async (request) => {
    setBusyId(`reminder-${request.id}`);
    setError("");
    setNotice("");
    try {
      const result = await sendAccountingReviewDocumentRequestReminder(token, request.id);
      const updated = result?.request;
      if (updated) {
        setRequests((current) =>
          current.map((item) => (item.id === updated.id ? updated : item))
        );
      }
      setNotice("Reminder saved for owner activity.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save this reminder.");
    } finally {
      setBusyId(null);
    }
  };

  const handleFollowupStatus = async (request, followupStatus) => {
    setBusyId(`followup-${request.id}`);
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingReviewDocumentRequestFollowup(token, request.id, {
        followup_status: followupStatus,
      });
      setRequests((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setNotice("Follow-up status updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update follow-up status.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SalonBusinessCpaShell
      title="Client workboard"
      description="See which SalonBusinessCPA clients need setup, package review, missing records, or follow-up."
      actions={
        <button
          type="button"
          onClick={loadData}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-700">
          {notice}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Clients", totals.clients],
          ["Open records", totals.openRequests],
          ["Follow-up", totals.followupNeeded],
          ["Answers needed", totals.answersNeeded],
          ["Package review", totals.packageReview],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            ["active", "Active work"],
            ["setup", "Setup"],
            ["package", "Package"],
            ["requests", "Open records"],
            ["followup", "Follow-up"],
            ["all", "All clients"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${
                filter === value
                  ? "border-slate-950 bg-slate-950 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading client workboard...
          </div>
        ) : clients.length ? (
          clients.map((client) => (
            <article
              key={keyFor(client)}
              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-slate-950">
                      {client.salon_name || "Salon"}
                    </h2>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                      Salon ID {client.salon_id}
                    </span>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                      {client.accounting_access_status || "not started"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {client.business_name || "Business"}
                  </p>
                  <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Profile
                      </dt>
                      <dd className="mt-1 text-sm font-semibold text-slate-900">
                        {client.profile_label || "Not set"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Payroll staff
                      </dt>
                      <dd className="mt-1 text-sm font-semibold text-slate-900">
                        {client.payroll_staff_count != null
                          ? client.payroll_staff_count
                          : client.staff_count_range_label || "Not set"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Package
                      </dt>
                      <dd className="mt-1 text-sm font-semibold text-slate-900">
                        {client.selected_package_label || "Not selected"}
                      </dd>
                      <dd className="text-xs text-slate-500">
                        {formatMoney(client.selected_package_price_cents)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Next action
                      </dt>
                      <dd className="mt-1 text-sm font-semibold text-slate-900">
                        {client.next_step || "Monitor account"}
                      </dd>
                    </div>
                  </dl>
                  {client.package_mismatch_message ? (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      {client.package_mismatch_message}
                    </div>
                  ) : null}
                  {Array.isArray(client.missing_setup_items) &&
                  client.missing_setup_items.length ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-sm font-semibold text-slate-900">Setup items</p>
                      <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-600">
                        {client.missing_setup_items.slice(0, 5).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to={`/salonbusinesscpa/review?business_id=${client.business_id}&salon_id=${client.salon_id}`}
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Open review
                    </Link>
                    <Link
                      to="/salonbusinesscpa/review"
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Create request
                    </Link>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-2 text-center text-xs font-semibold text-slate-600">
                    <div className="rounded-2xl bg-slate-50 p-3">
                      <div className="text-lg text-slate-950">{client.openRequests.length}</div>
                      Open
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-3">
                      <div className="text-lg text-slate-950">{client.overdue_request_count}</div>
                      Overdue
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-3">
                      <div className="text-lg text-slate-950">{client.received_document_count}</div>
                      Received
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-3">
                      <div className="text-lg text-slate-950">{client.reviewing_document_count}</div>
                      Review
                    </div>
                  </div>
                  {client.openRequests.length ? (
                    client.openRequests.slice(0, 4).map((request) => (
                      <div
                        key={request.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-slate-950">
                            {request.title}
                          </h3>
                          <span
                            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                              followupTone[request.followup_status] ||
                              "border-slate-200 bg-white text-slate-600"
                            }`}
                          >
                            {request.followup_status_label || "Waiting for client"}
                          </span>
                          {request.has_unanswered_questions ? (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                              answers needed
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {request.period_label || request.document_type_label}
                          {request.due_date ? ` - Due ${formatDate(request.due_date)}` : ""}
                        </p>
                        {request.reminder_count ? (
                          <p className="text-xs leading-5 text-slate-500">
                            {request.reminder_count} reminder{request.reminder_count === 1 ? "" : "s"}
                            {request.last_reminder_at_utc
                              ? `, last ${formatDate(request.last_reminder_at_utc)}`
                              : ""}
                          </p>
                        ) : null}
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                          Assigned to{" "}
                          <span className="font-semibold text-slate-700">
                            {request.assigned_accountant_name ||
                              request.assigned_accountant_email ||
                              "Not assigned"}
                          </span>
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleReminder(request)}
                            disabled={busyId === `reminder-${request.id}`}
                            className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                          >
                            {busyId === `reminder-${request.id}` ? "Saving..." : "Reminder"}
                          </button>
                          {followupOptions
                            .filter((option) => option.code !== request.followup_status)
                            .slice(0, 3)
                            .map((option) => (
                              <button
                                key={option.code}
                                type="button"
                                onClick={() => handleFollowupStatus(request, option.code)}
                                disabled={busyId === `followup-${request.id}`}
                                className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                              >
                                {option.label}
                              </button>
                            ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                      No open record requests for this client.
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No clients match this filter.
          </div>
        )}
      </section>
    </SalonBusinessCpaShell>
  );
}
